import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppData } from '../contexts/AppDataContext';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  BanknotesIcon,
  WrenchScrewdriverIcon,
  PlusIcon,
  EyeIcon,
  UserPlusIcon,
  ExclamationCircleIcon,
  ShoppingBagIcon,
  AcademicCapIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const Dashboard = () => {
  const { t } = useLanguage();
  const { 
    globalStats, 
    getLowStockProducts, 
    getMaintenanceNeeded,
    getEmployees,
    addNotification
  } = useAppData();

  // Obtenir les produits à stock faible
  const lowStockProducts = getLowStockProducts();
  
  // Obtenir les équipements nécessitant une maintenance
  const maintenanceNeeded = getMaintenanceNeeded();
  
  // Obtenir les employés
  const employees = getEmployees();
  
  // Exemple d'appel API qui serait remplacé par des vraies données
  const { data: dashboardData, isLoading } = useQuery('dashboard', async () => {
    try {
      const response = await axios.get('/dashboard');
      return response.data;
    } catch (err) {
      console.error('Erreur lors de la récupération des données:', err);
      // Si l'API échoue, utiliser des données fictives du contexte global
      return {
        stats: globalStats,
        revenueData: revenueChartData,
        membershipData: membershipChartData
      };
    }
  });

  // Données fictives pour les graphiques
  const revenueChartData = [
    { name: 'Jan', montant: 750000 },
    { name: 'Fév', montant: 820000 },
    { name: 'Mar', montant: 790000 },
    { name: 'Avr', montant: 830000 },
    { name: 'Mai', montant: 800000 },
    { name: 'Jun', montant: 860000 },
    { name: 'Jul', montant: 875000 }
  ];

  const membershipChartData = [
    { name: 'Mensuel', value: 190, color: '#3B82F6' },
    { name: 'Trimestriel', value: 80, color: '#8B5CF6' },
    { name: 'Annuel', value: 110, color: '#10B981' },
    { name: 'Quotidien', value: 20, color: '#F59E0B' }
  ];

  // Total pour calculer les pourcentages affichés dans la légende personnalisée
  const totalMemberships = membershipChartData.reduce((sum, m) => sum + m.value, 0);

  // Exemple de notification lors du chargement de la page (démonstration)
  useEffect(() => {
    if (lowStockProducts.length > 0) {
      addNotification({
        type: 'warning',
        title: 'Stock faible',
        message: `${lowStockProducts.length} produits en stock faible`,
        link: '/stock'
      });
    }

    if (maintenanceNeeded.length > 0) {
      addNotification({
        type: 'warning',
        title: 'Maintenance requise',
        message: `${maintenanceNeeded.length} équipements nécessitent une maintenance`,
        link: '/equipment'
      });
    }
  }, [lowStockProducts.length, maintenanceNeeded.length, addNotification]);

  // Stats principales
  const mainStats = [
    {
      title: 'Total des membres',
      value: globalStats.totalMembers,
      icon: UsersIcon,
      color: 'from-blue-500 to-blue-600',
      link: '/members',
      linkText: 'Voir tous',
    },
    {
      title: 'Membres actifs',
      value: globalStats.activeMembers,
      icon: UserPlusIcon,
      color: 'from-green-500 to-green-600',
      badge: '+5% ce mois',
    },
    {
      title: 'Revenus mensuels',
      value: `${globalStats.totalRevenue.toLocaleString('fr-DZ')} DA`,
      icon: BanknotesIcon,
      color: 'from-amber-500 to-amber-600',
      link: '/benefits',
      linkText: 'Voir analyses',
    },
    {
      title: 'État des équipements',
      value: globalStats.equipmentStatus,
      icon: WrenchScrewdriverIcon,
      color: 'from-purple-500 to-purple-600',
      link: '/equipment',
      linkText: 'Voir détails',
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">
            Tableau de bord
          </h1>
          <p className="text-white/60 mt-2">
            Vue d'ensemble de votre salle de musculation
          </p>
        </div>
        <Link
          to="/members?action=add"
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Nouveau membre</span>
        </Link>
      </div>

      {/* Stats principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {mainStats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                {stat.link && (
                  <Link to={stat.link} className="text-blue-400 text-xs flex items-center mt-2">
                    <EyeIcon className="h-3 w-3 mr-1" />
                    {stat.linkText}
                  </Link>
                )}
                {stat.badge && (
                  <div className="text-green-400 text-xs flex items-center mt-2">
                    <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                    {stat.badge}
                  </div>
                )}
              </div>
              <div className={`p-4 bg-gradient-to-r ${stat.color} rounded-2xl`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Graphique des revenus */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <h2 className="text-xl font-semibold text-white mb-4">Revenus mensuels</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={revenueChartData}
                margin={{ top: 20, right: 20, left: 40, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="name" stroke="rgba(255,255,255,0.6)" />
                <YAxis
                  stroke="rgba(255,255,255,0.6)"
                  width={70}
                  tickFormatter={(value) => `${(value / 1000).toLocaleString('fr-DZ')}k`}
                />
                <Tooltip 
                  contentStyle={{ 
                    background: 'rgba(15, 23, 42, 0.8)', 
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                  itemStyle={{ color: '#fff' }}
                  formatter={(value) => [`${value.toLocaleString('fr-DZ')} DA`, 'Montant']}
                />
                <Bar dataKey="montant" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-end mt-2">
            <Link to="/benefits" className="text-blue-400 text-sm flex items-center">
              Voir toutes les analyses <EyeIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </motion.div>

        {/* Graphique des abonnements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <h2 className="text-xl font-semibold text-white mb-4">Types d'abonnements</h2>
          <div className="h-72 flex items-center">
            {/* Diagramme donut */}
            <ResponsiveContainer width="70%" height="100%">
              <PieChart>
                <Pie
                  data={membershipChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={2}
                  dataKey="value"
                  label={false}
                  labelLine={false}
                >
                  {membershipChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                  formatter={(value) => [`${value} membres`, 'Nombre']}
                />
              </PieChart>
            </ResponsiveContainer>

            {/* Légende personnalisée */}
            <div className="w-48 ml-6 space-y-2">
              {membershipChartData.map((entry) => {
                const percent = ((entry.value / totalMemberships) * 100).toFixed(0);
                return (
                  <div key={entry.name} className="flex items-center space-x-2">
                    <span
                      className="block w-3 h-3 rounded-full"
                      style={{ backgroundColor: entry.color }}
                    ></span>
                    <span className="text-white text-sm">{entry.name}</span>
                    <span className="text-white/60 text-xs ml-auto">{percent}%</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end mt-2">
            <Link to="/subscriptions" className="text-blue-400 text-sm flex items-center">
              Gérer les abonnements <EyeIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Alertes stocks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-white">Stock faible</h2>
            <div className="p-2 rounded-2xl bg-gradient-to-r from-red-500 to-red-600">
              <ExclamationCircleIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="space-y-3">
            {lowStockProducts.length > 0 ? (
              lowStockProducts.map((product) => (
                <div key={product.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex items-center">
                    <div className="p-2 bg-amber-500/20 rounded-lg">
                      <ShoppingBagIcon className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-white text-sm font-medium">{product.name}</p>
                      <p className="text-white/50 text-xs">{product.sku}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="text-red-400 font-medium">{product.stock} / {product.lowStockThreshold}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-white/50">
                <p>Aucun produit en stock faible</p>
              </div>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <Link to="/stock" className="text-blue-400 text-sm flex items-center">
              Gérer le stock <EyeIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </motion.div>

        {/* Maintenance équipement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-white">Maintenance</h2>
            <div className="p-2 rounded-2xl bg-gradient-to-r from-amber-500 to-amber-600">
              <WrenchScrewdriverIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="space-y-3">
            {maintenanceNeeded.length > 0 ? (
              maintenanceNeeded.map((equipment) => (
                <div key={equipment.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex items-center">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <WrenchScrewdriverIcon className="h-5 w-5 text-purple-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-white text-sm font-medium">{equipment.name}</p>
                      <p className="text-white/50 text-xs">{equipment.issue}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="text-white/60 text-xs">{new Date(equipment.lastMaintenance).toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-white/50">
                <p>Aucune maintenance requise</p>
              </div>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <Link to="/equipment" className="text-blue-400 text-sm flex items-center">
              Voir tous les équipements <EyeIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </motion.div>

        {/* Personnel de service aujourd'hui */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-white">Personnel aujourd'hui</h2>
            <div className="p-2 rounded-2xl bg-gradient-to-r from-green-500 to-green-600">
              <AcademicCapIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="space-y-3">
            {employees.slice(0, 3).map((employee) => (
              <div key={employee.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/10">
                <div className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                    <UsersIcon className="h-4 w-4 text-white" />
                  </div>
                  <div className="ml-3">
                    <p className="text-white text-sm font-medium">{employee.firstName} {employee.lastName}</p>
                    <p className="text-white/50 text-xs">{employee.position}</p>
                  </div>
                </div>
                <div>
                  {employee.status === 'vacation' ? (
                    <span className="px-2 py-1 bg-amber-500/20 text-amber-300 text-xs rounded-full">
                      En congé
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs rounded-full">
                      {employee.schedule.split(':')[0]}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-4">
            <Link to="/employees" className="text-blue-400 text-sm flex items-center">
              Voir tout le personnel <EyeIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard; 